/**
 * Wire types — Brief 5 B4 Relatórios LGPD (Sprint Frontend Brief 5 · Tela 1).
 *
 * Hand-written (decisão do projeto) espelhando o contrato REAL do Engine
 * (apps/engine/src/modules/lgpd/lgpd-reports.service.ts + dto/lgpd-report.dto.ts
 * + packages/api-contracts/openapi/v1/paths/lgpd-reports.yaml). Triangulado
 * campo-a-campo (R15) — NÃO derivado do mockup.
 *
 * Gate: `lgpd-requests-management`. Tipos forenses (cascade-chain,
 * anonymization-log) exigem `audit-events-read-sensitive` (descriptor
 * `availableToCaller=false` + HTTP 403 no generate sem a cap).
 *
 * Integridade: hash SHA-256 SEMPRE presente; assinatura ICP-Brasil DIFERIDA
 * (`integrity.signed=false`, P-4) — NÃO renderizar como assinado.
 */

// ─── Tipos de relatório (enum do contrato) ────────────────────────────────────

export const LGPD_REPORT_TYPES = [
  'titular-export',
  'cascade-chain',
  'period-summary',
  'compliance-attestation',
  'anonymization-log',
] as const;

export type LgpdReportType = (typeof LGPD_REPORT_TYPES)[number];

/** Descriptor retornado por GET /v1/lgpd/reports/types. */
export interface LgpdReportTypeDescriptor {
  id: LgpdReportType;
  label: string;
  hint: string;
  forensicLevel: boolean;
  /** false quando forense e o caller não tem `audit-events-read-sensitive`. */
  availableToCaller: boolean;
}

// ─── Request de geração (enums do DTO) ────────────────────────────────────────

export const REPORT_GRANULARITIES = ['titular', 'operationId', 'period', 'requestType'] as const;
export type ReportGranularity = (typeof REPORT_GRANULARITIES)[number];

export const REPORT_PERIODS = ['30d', '90d', '1y', 'custom'] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export const LGPD_REQUEST_TYPES = [
  'access',
  'correction',
  'anonymization',
  'portability',
  'opposition',
  'consent_withdrawal',
] as const;
export type LgpdReportRequestType = (typeof LGPD_REQUEST_TYPES)[number];

export type ReportFormat = 'json' | 'pdf';

export interface GenerateLgpdReportRequest {
  reportType: LgpdReportType;
  granularity: ReportGranularity;
  period?: ReportPeriod;
  periodStart?: string;
  periodEnd?: string;
  requesterPersonId?: string;
  operationId?: string;
  requestType?: LgpdReportRequestType;
  format?: ReportFormat;
}

// ─── Resposta (GeneratedLgpdReport) ───────────────────────────────────────────

export interface ReportDocumentSection {
  heading: string;
  items: Record<string, unknown>[];
}

export interface ReportDocument {
  title: string;
  header: Record<string, unknown>;
  sections: ReportDocumentSection[];
}

export interface GeneratedLgpdReport {
  reportType: LgpdReportType;
  operationId: string;
  generatedAt: string;
  tenantId: string;
  scope: {
    granularity: ReportGranularity;
    period: ReportPeriod;
    periodStart: string;
    periodEnd: string;
    requesterPersonId: string | null;
    requestType: string | null;
  };
  document: ReportDocument;
  integrity: { algorithm: 'sha256'; hash: string; signed: boolean };
  format: ReportFormat;
}

// ─── Mapa UI → enum (DRIFT documentado no handoff §2) ─────────────────────────
//
// O mockup expõe granularidade com options `opid/periodo/tipo`; o BE usa
// `operationId/period/requestType`. Estes labels mapeiam para o enum real.

export const GRANULARITY_LABEL: Record<ReportGranularity, string> = {
  titular: 'Por titular específico',
  operationId: 'Por cadeia de operação',
  period: 'Por período',
  requestType: 'Por tipo de pedido',
};

export const PERIOD_LABEL: Record<ReportPeriod, string> = {
  '30d': 'Últimos 30 dias',
  '90d': 'Últimos 90 dias',
  '1y': 'Último ano',
  custom: 'Customizado',
};

export const REQUEST_TYPE_LABEL: Record<LgpdReportRequestType, string> = {
  access: 'Acesso',
  correction: 'Correção',
  anonymization: 'Anonimização',
  portability: 'Portabilidade',
  opposition: 'Oposição',
  consent_withdrawal: 'Revogação de consentimento',
};

/**
 * Granularidade default por tipo de relatório, alinhada às refines do DTO:
 *   - titular-export  → requer requesterPersonId  → granularidade `titular`
 *   - cascade-chain   → requer operationId        → granularidade `operationId`
 *   - demais          → baseados em período       → granularidade `period`
 */
export function defaultGranularityFor(reportType: LgpdReportType): ReportGranularity {
  if (reportType === 'titular-export') return 'titular';
  if (reportType === 'cascade-chain') return 'operationId';
  return 'period';
}
